import React, { useState, useEffect } from 'react';
import Editor from '@monaco-editor/react';
import axios from 'axios';
import './App.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Default template
const defaultTemplate = {
  content: [
    { text: 'Hello PDFMake!', style: 'header' },
    { text: 'This is a real-time PDF editor', style: 'subheader' },
    '\n',
    { text: 'Features:', style: 'quote' },
    {
      ul: [
        'Real-time PDF preview',
        'Template management',
        'Auto-save functionality',
        'Code editor with syntax highlighting'
      ]
    },
    '\n',
    {
      table: {
        headerRows: 1,
        widths: ['*', 'auto', 100],
        body: [
          ['Column 1', 'Column 2', 'Column 3'],
          ['One value', 'Another value', 'Third value'],
          ['Sample', 'Data', 'Here']
        ]
      }
    }
  ],
  styles: {
    header: {
      fontSize: 22,
      bold: true,
      color: '#2196F3'
    },
    subheader: {
      fontSize: 16,
      italics: true,
      color: '#666'
    },
    quote: {
      fontSize: 14,
      bold: true,
      margin: [0, 10, 0, 5]
    }
  },
  defaultStyle: {
    font: 'Roboto'
  }
};

function App() {
  const [code, setCode] = useState(JSON.stringify(defaultTemplate, null, 2));
  const [pdfUrl, setPdfUrl] = useState('');
  const [templates, setTemplates] = useState([]);
  const [currentTemplateId, setCurrentTemplateId] = useState('');
  const [templateName, setTemplateName] = useState('Untitled Template');
  const [autoSave, setAutoSave] = useState(true);
  const [lastSaved, setLastSaved] = useState(null);
  const [error, setError] = useState('');
  const [saveStatus, setSaveStatus] = useState('');

  // Load templates on mount
  useEffect(() => {
    loadTemplates();
    generatePDF(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-save on code change
  useEffect(() => {
    if (autoSave && currentTemplateId) {
      const timer = setTimeout(() => {
        saveTemplate(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, autoSave, currentTemplateId]);

  // Generate PDF on code change
  useEffect(() => {
    const timer = setTimeout(() => {
      generatePDF(code);
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const loadTemplates = async () => {
    try {
      const response = await axios.get(`${API_URL}/templates`);
      setTemplates(response.data);
    } catch (err) {
      console.error('Error loading templates:', err);
    }
  };

  const generatePDF = async (docCode) => {
    try {
      const docDefinition = JSON.parse(docCode);
      const response = await axios.post(
        `${API_URL}/generate-pdf`,
        { docDefinition },
        { responseType: 'blob' }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
      
      setPdfUrl(url);
      setError('');
    } catch (err) {
      setError(`Error: ${err.message}`);
      console.error('Error generating PDF:', err);
    }
  };

  const saveTemplate = async (showMessage = true) => {
    try {
      const docDefinition = JSON.parse(code);
      const id = currentTemplateId || `template_${Date.now()}`;
      
      await axios.post(`${API_URL}/templates`, {
        id,
        name: templateName,
        docDefinition
      });
      
      setCurrentTemplateId(id);
      setLastSaved(new Date());
      
      if (showMessage) {
        setSaveStatus('Saved successfully!');
        setTimeout(() => setSaveStatus(''), 3000);
      }
      
      await loadTemplates();
    } catch (err) {
      console.error('Error saving template:', err);
      setSaveStatus('Error saving template');
      setTimeout(() => setSaveStatus(''), 3000);
    }
  };

  const loadTemplate = async (id) => {
    try {
      const response = await axios.get(`${API_URL}/templates/${id}`);
      const template = response.data;
      
      setCode(JSON.stringify(template.docDefinition, null, 2));
      setTemplateName(template.name);
      setCurrentTemplateId(template.id);
      setLastSaved(new Date(template.updatedAt));
    } catch (err) {
      console.error('Error loading template:', err);
    }
  };

  const createNewTemplate = () => {
    setCode(JSON.stringify(defaultTemplate, null, 2));
    setTemplateName('Untitled Template');
    setCurrentTemplateId('');
    setLastSaved(null);
  };

  const deleteTemplate = async (id) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      try {
        await axios.delete(`${API_URL}/templates/${id}`);
        await loadTemplates();
        
        if (currentTemplateId === id) {
          createNewTemplate();
        }
      } catch (err) {
        console.error('Error deleting template:', err);
      }
    }
  };

  const downloadPDF = async () => {
    try {
      const docDefinition = JSON.parse(code);
      const response = await axios.post(
        `${API_URL}/download-pdf`,
        { 
          docDefinition,
          filename: `${templateName.replace(/\s+/g, '_')}.pdf`
        },
        { responseType: 'blob' }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${templateName.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading PDF:', err);
    }
  };

  const formatCode = () => {
    try {
      const parsed = JSON.parse(code);
      setCode(JSON.stringify(parsed, null, 2));
    } catch (err) {
      console.error('Error formatting code:', err);
    }
  };

  const downloadTemplate = () => {
    try {
      const docDefinition = JSON.parse(code);
      const template = {
        id: currentTemplateId || `template_${Date.now()}`,
        name: templateName,
        docDefinition,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      const dataStr = JSON.stringify(template, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${templateName.replace(/\s+/g, '_')}_template.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setSaveStatus('Template downloaded!');
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (err) {
      console.error('Error downloading template:', err);
      setSaveStatus('Error downloading template');
      setTimeout(() => setSaveStatus(''), 3000);
    }
  };

  const uploadTemplate = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const template = JSON.parse(event.target.result);
          
          // Validate template structure
          if (!template.docDefinition) {
            throw new Error('Invalid template format');
          }
          
          setCode(JSON.stringify(template.docDefinition, null, 2));
          setTemplateName(template.name || 'Imported Template');
          setCurrentTemplateId('');
          setLastSaved(null);
          
          setSaveStatus('Template uploaded successfully!');
          setTimeout(() => setSaveStatus(''), 3000);
        } catch (err) {
          console.error('Error uploading template:', err);
          setSaveStatus('Error: Invalid template file');
          setTimeout(() => setSaveStatus(''), 3000);
        }
      };
      
      reader.readAsText(file);
    };
    
    input.click();
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>PDFMake Real-time Editor</h1>
        <div className="header-controls">
          <input
            type="text"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            className="template-name-input"
            placeholder="Template Name"
          />
          <button onClick={() => saveTemplate(true)} className="btn btn-primary">
            Save Template
          </button>
          <button onClick={createNewTemplate} className="btn btn-secondary">
            New Template
          </button>
          <button onClick={downloadPDF} className="btn btn-success">
            Download PDF
          </button>
          <button onClick={downloadTemplate} className="btn btn-info">
            Download Template
          </button>
          <button onClick={uploadTemplate} className="btn btn-info">
            Upload Template
          </button>
          <button onClick={formatCode} className="btn btn-secondary">
            Format Code
          </button>
          <label className="auto-save-toggle">
            <input
              type="checkbox"
              checked={autoSave}
              onChange={(e) => setAutoSave(e.target.checked)}
            />
            Auto-save
          </label>
        </div>
        {saveStatus && <div className="save-status">{saveStatus}</div>}
        {lastSaved && (
          <div className="last-saved">
            Last saved: {lastSaved.toLocaleTimeString()}
          </div>
        )}
      </header>

      <div className="main-container">
        <aside className="sidebar">
          <h3>Templates</h3>
          <div className="templates-list">
            {templates.map((template) => (
              <div
                key={template.id}
                className={`template-item ${
                  currentTemplateId === template.id ? 'active' : ''
                }`}
              >
                <span onClick={() => loadTemplate(template.id)}>
                  {template.name}
                </span>
                <button
                  onClick={() => deleteTemplate(template.id)}
                  className="delete-btn"
                  title="Delete template"
                >
                  ×
                </button>
              </div>
            ))}
            {templates.length === 0 && (
              <p className="no-templates">No templates yet</p>
            )}
          </div>
        </aside>

        <div className="content-area">
          <div className="editor-container">
            <h3>Document Definition (JSON)</h3>
            <Editor
              height="100%"
              defaultLanguage="json"
              value={code}
              onChange={(value) => setCode(value || '')}
              theme="vs-dark"
              options={{
                minimap: { enabled: false },
                fontSize: 14,
                lineNumbers: 'on',
                scrollBeyondLastLine: false,
                automaticLayout: true,
                tabSize: 2
              }}
            />
            {error && <div className="error-message">{error}</div>}
          </div>

          <div className="preview-container">
            <h3>PDF Preview (Real-time)</h3>
            {pdfUrl ? (
              <iframe
                src={pdfUrl}
                title="PDF Preview"
                className="pdf-iframe"
              />
            ) : (
              <div className="loading">Loading preview...</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
